import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getHomeRouteByRole } from "@/lib/api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("ceap_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Validate token on mount
    const token = localStorage.getItem("ceap_token");
    if (token && !user) {
      api.get("/auth/me").then(({ data }) => {
        setUser(data);
        localStorage.setItem("ceap_user", JSON.stringify(data));
      }).catch(() => {
        localStorage.removeItem("ceap_token");
        localStorage.removeItem("ceap_user");
        setUser(null);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("ceap_token", data.token);
      localStorage.setItem("ceap_user", JSON.stringify(data.user));
      setUser(data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("ceap_token");
    localStorage.removeItem("ceap_user");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const ProtectedRoute = ({ children, roles }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    // Force password change on first login (unless already on the change-password page)
    if (user.must_change_password && window.location.pathname !== "/trocar-senha") {
      navigate("/trocar-senha", { replace: true });
      return;
    }
    if (roles && !roles.includes(user.role)) {
      navigate(getHomeRouteByRole(user.role), { replace: true });
    }
  }, [user, roles, navigate]);

  if (!user) return null;
  if (user.must_change_password && window.location.pathname !== "/trocar-senha") return null;
  if (roles && !roles.includes(user.role)) return null;
  return children;
};
